import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import LazyLoad from 'vanilla-lazyload'
import lazyloadConfig from '../config/lazyload'

// Only initialize it one time for the entire application
if (typeof document !== 'undefined' && !document.lazyLoadInstance) {
    document.lazyLoadInstance = new LazyLoad(lazyloadConfig)
}

const getCustomSrc = (src, dimens, type, isSlide) => {
    if (src) {
        // TODO: replace use of regex with faster method
        return src.replace(/\/\w+\.([a-z]+)$/, `/${dimens || 'original'}.${type || '$1'}`)
    }
    return getFallbackImageSrc(isSlide)
}

const getLowDefSrc = (src, type, isSlide) => {
    // carousel slides should be 21x9, all other images are 16x9
    const dimens = isSlide ? '21x9' : '20x11'

    return getCustomSrc(src, dimens, type, isSlide)
}

const getSrcSet = (src, type, isHero, isSlide) => {
    const formats = isHero || isSlide ? ['672', '1000', '1800', '2500', '3000'] : ['210', '360']

    // carousel slides should be 21x9, all other images are 16x9
    const heightMultiplier = isSlide ? 0.428571 : 0.5625

    return formats
        .map(fmt => {
            const height = Math.ceil(+fmt * heightMultiplier)
            const dimens = `${fmt}x${height}`

            return `${getCustomSrc(src, dimens, type, isSlide)} ${fmt}w`
        })
        .join(', ')
}

const getSizes = (isHero, isSlide) => {
    if (isHero || isSlide) {
        return '(max-width: 672px) 672px, (max-width: 1000px) 1000px, (max-width: 1800px) 1800px, (max-width: 2500px) 2500px, 3000px'
    }
    return '(max-width: 672px) 210px, 360px'
}

const getErrorHandler = (errored, setErrored) => {
    return ev => {
        if (!errored) {
            setErrored(true)
        }
    }
}

const getFallbackImage = (alt, isSlide, style, customClass) => {
    return <img alt={alt} src={getFallbackImageSrc(isSlide)} style={style} className={customClass} />
}

const getFallbackImageSrc = isSlide => {
    return isSlide ? '/video_thumbnail_placeholder21x9.png' : '/video_thumbnail_placeholder.png'
}

const CustomImage = ({ alt, customClass, imgStyle, imgSrc, isHero = false, isSlide = false }) => {
    const [errored, setErrored] = useState(false)

    useEffect(() => {
        document.lazyLoadInstance.update()
    }, [imgSrc])

    return (
        <ImageWrapper className={`${isHero ? 'hero' : isSlide ? 'slide' : 'card-img'}`}>
            <div className="add-padding"></div>
            {errored ? (
                getFallbackImage(alt, isSlide, imgStyle, customClass)
            ) : (
                <picture>
                    <source
                        type="image/webp"
                        srcSet={`${getLowDefSrc(imgSrc, 'webp', isSlide)} 21w`}
                        data-srcset={getSrcSet(imgSrc, 'webp', isHero, isSlide)}
                        data-sizes={getSizes(isHero, isSlide)}
                        onError={getErrorHandler(errored, setErrored)}
                        alt={alt}
                        style={imgStyle}
                        className={`lazy ${customClass || ''}`}
                    />
                    <img
                        alt={alt}
                        style={imgStyle}
                        onError={getErrorHandler(errored, setErrored)}
                        className={`lazy ${customClass || ''}`}
                        src={getLowDefSrc(imgSrc, undefined, isSlide)}
                        data-src={imgSrc}
                        data-srcset={getSrcSet(imgSrc, undefined, isHero, isSlide)}
                        data-sizes={getSizes(isHero, isSlide)}
                    />
                </picture>
            )}
        </ImageWrapper>
    )
}

const ImageWrapper = styled.div`
    position: relative;
    overflow: hidden;

    img {
        position: absolute;
        top: 0px;
        left: 0px;
        object-fit: cover;
        object-position: center center;
        opacity: 1;
        transition: none 0s ease 0s;
    }

    &.card-img {
        display: inline-block;
        width: 210px;
        height: 118px;

        img {
            width: 100%;
            height: 100%;
        }
    }

    &.hero {
        > div.add-padding {
            width: 100%;
            padding-bottom: 56.25%;
        }

        img {
            width: 75vw;
            height: 100%;
        }
    }

    &.slide {
        > div.add-padding {
            width: 100%;
            padding-bottom: 42.857142857142854%;
        }

        img {
            height: 100%;
            width: 100%;
        }
    }
`

export default CustomImage
